import React, { useRef, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Logo from 'assets/images/Logo blue.png';
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { SG_GET_CHAT_GPT_SUMMARY } from "constants/actions";
import { v4 as uuidv4 } from "uuid";
import { createSimplePrompt } from './SimpleStructurePrompt';

const SimpleConversation = ({ responses, setScreen, saveResponse, language }) => {
  const dispatch = useDispatch();
  const timeout = useRef(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState("");

  const { get_chat_gpt } = useSelector(
    (state) => ({
      get_chat_gpt: state.chatgpt,
    }),
    shallowEqual
  );

  // Initial load - fetch AI question
  useEffect(() => {
    setLoading(true);
    fetchQuestion();
  }, []);

  // Monitor AI response
  useEffect(() => {
    if (get_chat_gpt?.response_pending === true) {
      timeout.current = setTimeout(() => {
        setLoading(false);
      }, 15000);
    }

    if (get_chat_gpt?.response_pending === false) {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      
      if (get_chat_gpt?.chat_gpt_summary?.response) {
        setQuestion(get_chat_gpt.chat_gpt_summary.response);
        setLoading(false);
      } else {
        setLoading(false);
      }
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [get_chat_gpt]);

  const handleChange = (event) => {
    setResponse(event.target.value);
    const responseObj = {
      response: event.target.value,
      question: question,
      type: 'ai_followup'
    };
    saveResponse(responseObj);
  };

  const fetchQuestion = () => {
    const prompt = createSimplePrompt(responses, language);
    const validationToken = uuidv4();

    dispatch({
      type: SG_GET_CHAT_GPT_SUMMARY,
      payload: {
        payload: prompt,
        token: validationToken
      },
    });
  };

  const handleNext = () => {
    if (response.trim()) {
      setScreen();
    }
  };

  const getPlaceholderText = () => {
    switch(language) {
      case 'FR':
        return "Partagez vos pensées ici...";
      case 'ES':
        return "Comparta sus pensamientos aquí...";
      default:
        return "Share your thoughts here...";
    }
  };

  const getNextButtonText = () => {
    switch(language) {
      case 'FR':
        return "Suivant";
      case 'ES':
        return "Siguiente";
      default:
        return "Next";
    }
  };

  return (
    <Container>
      <TitleContainer>
        <Title>
          <IMG src={Logo} />
        </Title>
        <Subtitle>
          {language === 'EN' ? 'Feedback Survey' : 
           language === 'FR' ? 'Enquête de feedback' : 
           'Encuesta de retroalimentación'}
        </Subtitle>
      </TitleContainer>

      <ContentSection>
        <QuestionSection>
          {loading || !question ? (
            <SkeletonContainer>
              <SkeletonLine />
              <SkeletonLine width="92%" />
              <SkeletonLine width="88%" />
            </SkeletonContainer>
          ) : (
            <QuestionText>{question}</QuestionText>
          )}
        </QuestionSection>
        
        {!loading && question && (
          <>
            <StyledTextarea
              value={response}
              onChange={handleChange}
              placeholder={getPlaceholderText()}
            />

            <ButtonArea>
              <Button 
                onClick={handleNext} 
                disabled={!response.trim()}
              >
                {getNextButtonText()}
              </Button>
            </ButtonArea>
          </>
        )}
      </ContentSection>
    </Container>
  );
};

export default SimpleConversation;

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`;

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 600px;
  width: 100%;
  font-family: "Raleway", sans-serif;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
  width: 100%;
`;

const Title = styled.div`
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  color: #2D70E2;
`;

const IMG = styled.img`
  width: 120px;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #6B7280;
  margin-top: 5px;
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const QuestionSection = styled.div`
  width: 100%;
  min-height: 80px;
  margin-bottom: 20px;
`;

const QuestionText = styled.div`
  color: #2a3039;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.6;
  animation: ${fadeIn} 0.4s ease-out forwards;
`;

const StyledTextarea = styled.textarea`
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #2a3039;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  resize: none;
  font-family: "Raleway", sans-serif;
  height: 150px;
  animation: ${fadeIn} 0.5s ease-out forwards;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #2d70e2;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(45, 112, 226, 0.25);
  }
`;

const ButtonArea = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  color: white;
  font-size: 16px;
  font-weight: 400;
  border: none;
  border-radius: 5px;
  padding: 10px 35px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 40px;
  background-color: #2D70E2;
  
  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background-color: #ccc !important;
    cursor: not-allowed;
  }
`;

// Skeleton loader components
const SkeletonContainer = styled.div`
  width: 100%;
  display: block;
`;

const SkeletonLine = styled.div`
  display: block;
  height: 16px;
  margin-bottom: 12px;
  width: ${props => props.width || '100%'};
  background: #f6f7f8;
  background: linear-gradient(to right, #eee 2%, #ddd 18%, #eee 33%);
  background-size: 800px 100%;
  animation: ${shimmerAnimation} 1.2s infinite linear;
  border-radius: 2px;
`; 